/**
 * ラウドネス正規化 (書き出す MP4 全体の音量を目標 LUFS へ揃える) の正本。
 * docs/features/video-editor-capability-gaps-plan.md §4.3-M。
 *
 * 同じ意味・同じ既定値・同じクランプの実装が 3 か所にある (どれかを変えたら必ず 3 つとも変える):
 * - ここ (image-generator FE: UI・AI 提案の合成・書き出しの注記)
 * - video-editor リポジトリの src/schema/loudness.ts (Cloud Run の MP4 レンダラー)
 * - backend/services/video_edit/loudness.py (AI 提案の正規化・AI が読む要約・保存時検証)
 *
 * ## 意味
 * - `enabled`: 書き出し時に正規化を掛けるか (既定 false = 従来どおり無加工)
 * - `targetLufs`: 目標の統合ラウドネス (-24〜-8・0.5 刻み・既定 -14)
 * - `truePeakDb`: 許容する最大トゥルーピーク (-3〜0 dBTP・0.1 刻み・既定 -1)
 * - `lra`: 目標のラウドネスレンジ (省略可。1〜20)
 *
 * ## 適用範囲 (重要)
 * **MP4 書き出し (Cloud Run レンダラー) の後処理でだけ掛かる**。プレビュー (Player) には
 * 掛からない (ブラウザで ffmpeg の loudnorm 相当を再現しないため)。UI にもその旨を明記する。
 * Premiere XML / FCPXML には対応物が無いので注記だけ (§0-2)。
 *
 * ## 目安
 * YouTube / Instagram / TikTok などの配信は -14 LUFS 前後、テレビ CM は媒体規定 (日本では
 * -24 LKFS が一般的)。媒体規定 (`video_asset_specs`) にラウドネス値を持たせる件は将来 (§4.3-M)。
 */
export const LOUDNESS_TARGET_LUFS_MIN = -24;
export const LOUDNESS_TARGET_LUFS_MAX = -8;
export const LOUDNESS_TARGET_LUFS_STEP = 0.5;
export const LOUDNESS_TRUE_PEAK_DB_MIN = -3;
export const LOUDNESS_TRUE_PEAK_DB_MAX = 0;
export const LOUDNESS_TRUE_PEAK_DB_STEP = 0.1;
export const LOUDNESS_LRA_MIN = 1;
export const LOUDNESS_LRA_MAX = 20;
export const LOUDNESS_LRA_STEP = 0.5;

/**
 * 正規化後のラウドネス設定。`enabled` / `targetLufs` / `truePeakDb` は必ず埋まる
 * (保存形の `Loudness` は数値キーが省略可なので、使う側はこちらを見る)。
 */
export interface ResolvedLoudness {
  enabled: boolean;
  targetLufs: number;
  truePeakDb: number;
  lra?: number;
}

/** 既定値。`enabled: false` なので、設定を持たない既存プロジェクトの書き出しは一切変わらない。 */
export const LOUDNESS_DEFAULTS: Omit<ResolvedLoudness, 'lra'> = {
  enabled: false,
  targetLufs: -14,
  truePeakDb: -1,
};

/** UI の目標セレクタの選択肢 (配信 / 配信 (静かめ) / テレビ CM)。AI へのプロンプトでも目安に使う。 */
export const LOUDNESS_TARGET_PRESETS: ReadonlyArray<{ targetLufs: number; label: string }> = [
  { targetLufs: -14, label: '-14 (配信)' },
  { targetLufs: -16, label: '-16 (静かめ)' },
  { targetLufs: -23, label: '-23 (TV CM)' },
];

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** half-up の丸め (backend の loudness.py と同じ規則。-0 は 0 に正規化)。 */
function roundToStep(value: number, step: number): number {
  const inv = 1 / step;
  const rounded = Math.floor(value * inv + 0.5) / inv;
  return rounded === 0 ? 0 : rounded;
}

function clampNumber(value: unknown, min: number, max: number, fallback: number, step: number): number {
  if (!isNumber(value)) return fallback;
  return roundToStep(Math.min(max, Math.max(min, value)), step);
}

/**
 * 保存形 / AI の指定を正規化する。
 * `null` / オブジェクトでない値は `undefined` (設定なし)。欠けたキーは既定値で埋めるので、
 * 返り値は必ず enabled / targetLufs / truePeakDb を持つ (保存値 = 実効値)。
 * `lra` は指定があるときだけ残す。
 */
export function normalizeLoudness(raw: unknown): ResolvedLoudness | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const value = raw as Record<string, unknown>;
  const normalized: ResolvedLoudness = {
    enabled: value.enabled === true,
    targetLufs: clampNumber(
      value.targetLufs, LOUDNESS_TARGET_LUFS_MIN, LOUDNESS_TARGET_LUFS_MAX,
      LOUDNESS_DEFAULTS.targetLufs, LOUDNESS_TARGET_LUFS_STEP,
    ),
    truePeakDb: clampNumber(
      value.truePeakDb, LOUDNESS_TRUE_PEAK_DB_MIN, LOUDNESS_TRUE_PEAK_DB_MAX,
      LOUDNESS_DEFAULTS.truePeakDb, LOUDNESS_TRUE_PEAK_DB_STEP,
    ),
  };
  if (isNumber(value.lra)) {
    normalized.lra = clampNumber(
      value.lra, LOUDNESS_LRA_MIN, LOUDNESS_LRA_MAX, LOUDNESS_LRA_MIN, LOUDNESS_LRA_STEP,
    );
  }
  return normalized;
}

/** 実際に書き出しで正規化が掛かる設定か。 */
export function isLoudnessActive(raw: unknown): boolean {
  const loudness = normalizeLoudness(raw);
  return Boolean(loudness?.enabled);
}

/**
 * AI の全体提案 / ops が書いた loudness を保存形へ正規化する。
 * **オブジェクトを書いた = 掛けたい意図**なので、`enabled` は明示的に `false` と書かれた
 * ときだけ false にする (ducking の normalizeProposalDucking と同じ規則)。
 */
export function normalizeProposalLoudness(raw: unknown): ResolvedLoudness | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const merged = { ...(raw as Record<string, unknown>) };
  merged.enabled = merged.enabled !== false;
  return normalizeLoudness(merged);
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
}

/** 要約 / UI に出す 1 行 (backend の describe_loudness と同じ文言)。 */
export function loudnessLabel(raw: unknown): string {
  const loudness = normalizeLoudness(raw);
  if (!loudness || !loudness.enabled) return '無効';
  const lra = loudness.lra !== undefined ? ` / LRA ${formatNumber(loudness.lra)}` : '';
  return `有効 (${formatNumber(loudness.targetLufs)} LUFS / TP ${formatNumber(loudness.truePeakDb)} dB${lra})`;
}
